// src/views/Base/components/NavBar/components/SessionInfo/components/NewShiftModal/NewShiftModal.tsx

import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { X, ExclamationTriangleFill, ArrowClockwise } from 'react-bootstrap-icons';
import { createNewShift } from '../../../../../../../../crud/management_api/local_sessions';
import { getLocalCajaManagement } from '../../../../../../../../crud/management_api/locales_caja_management';
import { Pendings } from '../EndSession/components/Pendings';
import { PendingTable } from '../../../../../Panel/components/Pending/PendingTable';
import CreateCajaRecord from '../../../../../../../Restaurant/Caja/CajasManagement/components/Arqueos/components/CreateCajaRecord/CreateCajaRecord';
import ModalPendingCash from '../../../../../Panel/components/Pending/components/ModalPendingCash/ModalPendingCash';
import OpenCaja from '../../../../../../../Restaurant/Caja/CajasManagement/components/IndividualCaja/components/OpenCaja/OpenCaja';
import './NewShiftModal.scss';

interface NewShiftModalProps {
  show: boolean;
  handleClose: () => void;
  localSessionId: number;  // For checking pendings in the active session
  localId: number;         // For the createNewShift API (venue/local ID)
  userId: number;
  onShiftCreated?: () => void;
}

const NewShiftModal: React.FC<NewShiftModalProps> = ({
  show,
  handleClose,
  localSessionId,
  localId,
  userId,
  onShiftCreated
}) => {
  const [shiftName, setShiftName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [hasPendings, setHasPendings] = useState<boolean | null>(null);
  const [pendingsLoaded, setPendingsLoaded] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showArqueoModal, setShowArqueoModal] = useState(false); // Direct arqueo creation modal
  const [showPendingCashModal, setShowPendingCashModal] = useState(false); // Pending cash modal
  const [pendingCashData, setPendingCashData] = useState<any[]>([]); // Store pending cash data
  const [showOpenCajaModal, setShowOpenCajaModal] = useState(false); // Open caja modal after shift creation
  const [cajas, setCajas] = useState<any[]>([]); // Store cajas for OpenCaja modal
  
  // New state for transferable pendings (pedidos/recibos that can be moved to new shift)
  const [transferablePendings, setTransferablePendings] = useState<{ pedidos: number; recibos: number }>({ pedidos: 0, recibos: 0 });
  const [acknowledgedTransfer, setAcknowledgedTransfer] = useState(false);
  
  // Check if there are transferable items
  const hasTransferablePendings = transferablePendings.pedidos > 0 || transferablePendings.recibos > 0;

  // Callback when pendings are loaded
  const handlePendingsLoaded = (pendingsExist: boolean) => {
    setHasPendings(pendingsExist);
    setPendingsLoaded(true);
  };
  
  // Callback for transferable pendings
  const handleTransferablePendingsFound = (pendings: { pedidos: number; recibos: number }) => {
    setTransferablePendings(pendings);
  };

  // Callback to refresh pendings after resolution
  const handlePendingsResolved = () => {
    setHasPendings(null);
    setPendingsLoaded(false);
    setTransferablePendings({ pedidos: 0, recibos: 0 });
    setAcknowledgedTransfer(false);
  };

  // Handler when arqueo is created successfully from the direct modal
  const handleArqueoCreated = () => {
    setShowArqueoModal(false);
    // Refresh pendings to check if arqueo is now resolved
    handlePendingsResolved();
  };

  // Handler for pending cash modal
  const handlePendingCashClose = () => {
    setShowPendingCashModal(false);
    // Refresh pendings to check if cash is now resolved
    handlePendingsResolved();
  };

  const handleCreateShift = async () => {
    // Only block if there are actual blocking pendings (arqueo or efectivo in shift context)
    if (hasPendings) {
      toast.error('Debes resolver los pendientes bloqueantes (arqueo o efectivo) antes de crear un nuevo turno.');
      return;
    }
    
    // If there are transferable pendings, user must acknowledge
    if (hasTransferablePendings && !acknowledgedTransfer) {
      toast.error('Debes confirmar que aceptas transferir los pedidos y recibos pendientes al nuevo turno.');
      return;
    }

    setIsCreating(true);
    try {
      // Pass force_with_pendings if there are transferable items and user acknowledged
      const forceWithPendings = hasTransferablePendings && acknowledgedTransfer;
      await createNewShift(localId, userId, shiftName || undefined, forceWithPendings);
      toast.success('Turno creado exitosamente');
      
      // Wait for the session to refresh before closing the modal
      if (onShiftCreated) {
        await onShiftCreated();
      }
      
      // Load cajas and show OpenCaja modal
      try {
        const cajasResponse = await getLocalCajaManagement(localId, userId);
        const allCajas = cajasResponse.data || [];
        // Find the first closed caja to open
        const closedCaja = allCajas.find((c: any) => !c.is_open);
        if (closedCaja) {
          setCajas(allCajas);
          setShiftName('');
          setAcknowledgedTransfer(false);
          handleClose();
          // Show OpenCaja modal after a brief delay
          setTimeout(() => {
            setShowOpenCajaModal(true);
          }, 300);
        } else {
          // No closed cajas to open, just close and reload
          setShiftName('');
          setAcknowledgedTransfer(false);
          handleClose();
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } catch (error) {
        console.error('Error loading cajas:', error);
        // If we can't load cajas, just close and reload
        setShiftName('');
        setAcknowledgedTransfer(false);
        handleClose();
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error: any) {
      console.error('Error creating shift:', error);
      toast.error(error?.response?.data?.message || 'Error al crear el turno');
    } finally {
      setIsCreating(false);
    }
  };

  const handleModalClose = () => {
    setShiftName('');
    setAcknowledgedTransfer(false);
    setTransferablePendings({ pedidos: 0, recibos: 0 });
    handleClose();
  };

  return (
    <>
    <Modal 
      show={show && !showArqueoModal && !showPendingCashModal} 
      onHide={handleModalClose}
      className="new-shift-modal"
      centered
      backdrop="static"
      keyboard={true}
      scrollable
    >
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>Crear Nuevo Turno</Modal.Title>
        </div>
        <Button variant="none" onClick={handleModalClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {/* Check for blocking pending items (arqueo and efectivo in shift context) */}
        <Pendings 
          localSessionId={localSessionId} 
          onPendingsLoaded={handlePendingsLoaded}
          excludeCajas={true}
          context="shift"
          onTransferablePendingsFound={handleTransferablePendingsFound}
          onArqueoAction={() => setShowArqueoModal(true)}
          onEfectivoAction={() => setShowPendingCashModal(true)}
        />

        {/* Show transferable pendings info when no blocking pendings but there are pedidos/recibos */}
        {pendingsLoaded && !hasPendings && hasTransferablePendings && (
          <div className="transferable-pendings-section">
            <div className="transferable-pendings-header">
              <ExclamationTriangleFill className="warning-icon" />
              <div className="transferable-pendings-info">
                <h5>Elementos que serán transferidos</h5>
                <p>Los siguientes elementos pendientes serán movidos al nuevo turno:</p>
              </div>
            </div>
            
            <div className="transferable-pendings-summary">
              {transferablePendings.pedidos > 0 && (
                <div className="transferable-item">
                  <span className="transferable-label">Pedidos pendientes:</span>
                  <span className="transferable-count">{transferablePendings.pedidos}</span>
                </div>
              )}
              {transferablePendings.recibos > 0 && (
                <div className="transferable-item">
                  <span className="transferable-label">Recibos activos:</span>
                  <span className="transferable-count">{transferablePendings.recibos}</span>
                </div>
              )}
            </div>
            
            <div className="acknowledge-transfer-section">
              <Form.Check 
                type="checkbox"
                id="acknowledge-transfer"
                className="acknowledge-checkbox"
                checked={acknowledgedTransfer}
                onChange={(e) => setAcknowledgedTransfer(e.target.checked)}
                disabled={isCreating}
                label={
                  <span className="acknowledge-label">
                    Entiendo que los pedidos y recibos pendientes serán transferidos al nuevo turno. 
                    <strong> Esta operación no es reversible.</strong>
                  </span>
                }
              />
            </div>
          </div>
        )}

        {/* Only show the form if no blocking pendings and either no transferables or acknowledged */}
        {pendingsLoaded && !hasPendings && (!hasTransferablePendings || acknowledgedTransfer) && (
          <>
            <div className="shift-confirmation">
              <p className="shift-description">
                Al crear un nuevo turno, el turno actual se cerrará automáticamente 
                y comenzará uno nuevo con la información que proporciones.
              </p>
            </div>
            <Form>
              <Form.Group>
                <Form.Label>Nombre del Turno (Opcional)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej: Turno de Tarde, Turno Vespertino..."
                  value={shiftName}
                  onChange={(e) => setShiftName(e.target.value)}
                  disabled={isCreating}
                />
                <Form.Text className="text-muted">
                  Si no especificas un nombre, se generará uno automáticamente
                </Form.Text>
              </Form.Group>
            </Form>
          </>
        )}
          {/* (Buttons moved to footer to avoid duplication) */}
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button 
            variant="none"
            className="form-button-cancel"
            onClick={handleModalClose}
            disabled={isCreating}
          >
            Cancelar
          </Button>
          
          {/* Show create button when:
              1. No blocking pendings AND no transferable pendings, OR
              2. No blocking pendings AND user has acknowledged the transfer */}
          {pendingsLoaded && !hasPendings && (!hasTransferablePendings || acknowledgedTransfer) && (
            <Button 
              variant="none"
              className="form-button-create"
              onClick={handleCreateShift}
              disabled={isCreating}
            >
              {isCreating ? 'Creando...' : 'Crear Turno'}
            </Button>
          )}

          {/* When there are blocking pendings (arqueo), only show "Verificar nuevamente" button */}
          {pendingsLoaded && hasPendings && (
            <Button
              variant="none"
              className="form-button-secondary"
              onClick={handlePendingsResolved}
              disabled={isCreating}
            >
              <ArrowClockwise size={16} style={{ marginRight: '0.5rem' }} />
              Verificar nuevamente
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>

      {/* Pending modal reused from EndSession */}
      <Modal
        show={showPendingModal}
        onHide={() => setShowPendingModal(false)}
        centered
        size="lg"
      >
        <Modal.Header>
          <div className='form-modal-title-container'>
            <Modal.Title className='form-modal-title'>Pendientes</Modal.Title>
          </div>
          <Button variant="none" onClick={() => setShowPendingModal(false)}>
            <X size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'none' }} data-debug="pending-local-id">{localSessionId}</div>
          <PendingTable
            localSessionId={localSessionId ?? undefined}
            setActioncaja={() => setShowPendingModal(false)}
            onNavigate={() => setShowPendingModal(false)}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <Button variant='none' className="form-button-cancel" onClick={() => setShowPendingModal(false)}>
              Cerrar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Direct Arqueo Creation Modal - opens CreateCajaRecord directly */}
      {showArqueoModal && (
        <CreateCajaRecord
          show={showArqueoModal}
          setShow={setShowArqueoModal}
          onRecordCreated={handleArqueoCreated}
          hideCloseSession={true}
        />
      )}

      {/* Pending Cash Modal */}
      {showPendingCashModal && (
        <ModalPendingCash
          show={showPendingCashModal}
          onHide={handlePendingCashClose}
          cajas={pendingCashData}
          setCajas={setPendingCashData}
        />
      )}

      {/* Open Caja Modal - shown after shift creation */}
      {showOpenCajaModal && cajas.length > 0 && (
        <OpenCaja
          caja={cajas.find((c: any) => !c.is_open) || cajas[0]}
          cajas={cajas}
          setCajas={setCajas}
          show={showOpenCajaModal}
          setShow={(show: boolean) => {
            setShowOpenCajaModal(show);
            if (!show) {
              // Reload page after closing OpenCaja modal
              setTimeout(() => {
                window.location.reload();
              }, 300);
            }
          }}
        />
      )}
  </>
  );
};

export default NewShiftModal;
